import { useEffect, type ReactNode } from 'react';
import { useSetAtom } from 'jotai';
import { authTokensAtom, authUserAtom, authLoadingAtom } from '../atoms/authAtoms';
import { useAuth } from '../features/auth/useAuth';
import type { AuthTokens, AuthUser } from '../types';

interface AuthProviderProps {
  children: ReactNode;
}

// Simple dev-only mock user used when VITE_DEV_BYPASS_AUTH is enabled.
const DEV_MOCK_USER: AuthUser = {
  id: 'dev-user-1',
  email: 'dev@consentropy.local',
  name: 'Dev User',
  role: 'platform_admin',
  companyId: 'dev-company',
};

const DEV_MOCK_TOKENS: AuthTokens = {
  idToken: 'dev-id-token',
  accessToken: 'dev-access-token',
  refreshToken: 'dev-refresh-token',
  expiresAt: Date.now() + 1000 * 60 * 60 * 24, // 24h
};

export function AuthProvider({ children }: AuthProviderProps) {
  const { restoreSession } = useAuth();
  const setTokens = useSetAtom(authTokensAtom);
  const setUser = useSetAtom(authUserAtom);
  const setLoading = useSetAtom(authLoadingAtom);

  useEffect(() => {
    const bypass = import.meta.env.VITE_DEV_BYPASS_AUTH === 'true';

    if (bypass && !import.meta.env.DEV) {
      throw new Error(
        'VITE_DEV_BYPASS_AUTH is set in a non-development build. ' +
          'This env var must only be used during local development.',
      );
    }

    if (bypass) {
      // Set mock auth state for developer convenience
      setTokens(DEV_MOCK_TOKENS);
      setUser(DEV_MOCK_USER);
      setLoading(false);
      return;
    }

    void restoreSession();
  }, [restoreSession, setTokens, setUser, setLoading]);

  return <>{children}</>;
}
